// --- React ---
import React, { useState, useEffect } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 공통 UI 컴포넌트 ---
import { ModalShell, FormStack, FormField, FormLabel } from '@/components/ui';
import { Button, Input, SelectBox } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';

// --- 스토어 ---
import { useNotificationStore } from '../../../store/notificationStore';
import { useAppStore } from '../../../store/loadingStore';

// --- 유효성 검사 · 에러 파싱 ---
import { parseCompanyApiError } from '@/utils/errors';

// --- API ---
import { useCreateSite, useUpdateSite } from '@/api/site/useSite';
import { useGetSiteManagers, type SiteManager } from '@/api/company/useSiteManagers';

// --- 타입 ---
import type { SiteResponse } from '@packages/types/site/dto';

// --- 스타일 ---
import styled from '@emotion/styled';

// ==================== 상수 ====================

/** Stable fallback when the query has no `data` yet — a fresh `[]` each render breaks useEffect deps. */
const EMPTY_SITE_MANAGERS: SiteManager[] = [];

// ==================== 타입 정의 ====================

interface SiteCrudModalProps {
  isOpen: boolean;
  site: SiteResponse | null;
  companyId: number;
  onClose: () => void;
  onSave: (site: SiteResponse) => void;
}

// ==================== 스타일 컴포넌트 ====================

const LoadingText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// ==================== 컴포넌트 ====================

export const SiteCrudModal: React.FC<SiteCrudModalProps> = ({
  isOpen,
  site,
  companyId,
  onClose,
  onSave,
}) => {
  // ==================== 다국어 ====================
  const { t } = useTranslation();

  // ==================== 스토어 ====================
  const { showSuccess, showError } = useNotificationStore();
  const { showLoading, hideLoading } = useAppStore();

  // ==================== 상태 관리 ====================
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    telephone: '',
    site_manager_id: null as number | null,
  });
  const [telephoneValid, setTelephoneValid] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ==================== API 훅 ====================
  const createMutation = useCreateSite(companyId);
  const updateMutation = useUpdateSite(site?.id || 0, companyId);
  const { data: siteManagers = EMPTY_SITE_MANAGERS, isLoading: loadingSiteManagers } =
    useGetSiteManagers(companyId, { enabled: isOpen && !!companyId });

  // ==================== 이펙트 ====================

  /**
   * 모달 열림 / 사이트·매니저 목록 변경 시 폼 동기화
   * - 수정 모드: 기존 사이트 정보 + 현재 매니저 탐색
   * - 생성 모드: 빈 폼 초기화
   */
  useEffect(() => {
    if (!isOpen) return;
    setTelephoneValid(true);
    setErrors({});
    if (site) {
      const currentManager = siteManagers.find(
        m => m.site_id === site.id || m.sites?.some(s => s.id === site.id)
      );
      setFormData({
        name: site.name ?? '',
        location: site.location ?? '',
        telephone: normalizePhone(site.telephone ?? ''),
        site_manager_id: currentManager?.id ?? null,
      });
    } else {
      setFormData({ name: '', location: '', telephone: '', site_manager_id: null });
    }
  }, [isOpen, site, siteManagers]);

  // ==================== 유효성 검사 ====================

  /** 필수 필드(사이트명) 유효성 검사 */
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = 'company:site_name_required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== 핸들러 ====================

  /**
   * 저장 핸들러
   * 1. 유효성 검사
   * 2. 수정 모드 → updateMutation / 생성 모드 → createMutation 호출
   * 3. 성공/실패 알림
   */
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      showLoading();
      const name = formData.name.trim();
      const location = formData.location.trim() || undefined;
      const telephone = formData.telephone || undefined;

      if (site) {
        // null은 JSON으로 명시 전달해야 함. || undefined 쓰면 해제가 본문에서 빠져 서버가 기존 매니저를 유지함
        await updateMutation.mutateAsync({
          name,
          location,
          telephone,
          site_manager_id: formData.site_manager_id,
        });
        showSuccess('company:site_update_success');
      } else {
        await createMutation.mutateAsync({
          name,
          location,
          telephone,
          ...(formData.site_manager_id != null ? { site_manager_id: formData.site_manager_id } : {}),
        });
        showSuccess('company:site_create_success');
      }

      onSave({} as SiteResponse);
      onClose();
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  /** 모달 닫기 — 폼 데이터 초기화 후 부모 onClose 호출 */
  const handleClose = () => {
    setFormData({ name: '', location: '', telephone: '', site_manager_id: null });
    setTelephoneValid(true);
    setErrors({});
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen}
      title={site ? t(i18nKeys.company.site_edit) : t(i18nKeys.company.site_create_new)}
      onClose={handleClose}
      size="md"
      footer={
        <>
          {/* 취소 버튼 */}
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            {t(i18nKeys.company.cancel)}
          </Button>
          {/* 저장 버튼 */}
          <Button onClick={handleSave} disabled={isPending || (!!formData.telephone && !telephoneValid)}>
            {isPending ? t(i18nKeys.company.processing) : t(i18nKeys.company.save)}
          </Button>
        </>
      }
    >
      <FormStack>
        {/* 사이트명 (필수) */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.site_name)} *</FormLabel>
          <Input
            value={formData.name}
            onChange={value => {
              setFormData({ ...formData, name: value });
              setErrors({ ...errors, name: '' });
            }}
            placeholder={t(i18nKeys.company.site_name_placeholder)}
            error={errors.name}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 위치 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.site_location)}</FormLabel>
          <Input
            value={formData.location}
            onChange={value => setFormData({ ...formData, location: value })}
            placeholder={t(i18nKeys.company.site_location_placeholder)}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 전화번호 */}
        <FormField>
          <PhoneInput
            label={t(i18nKeys.company.site_telephone)}
            value={formData.telephone}
            onChange={(value, isValid) => {
              setFormData({ ...formData, telephone: value });
              setTelephoneValid(isValid);
            }}
            fullWidth
          />
        </FormField>

        {/* 사이트 매니저 선택 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.site_manager_select)}</FormLabel>
          <SelectBox
            value={formData.site_manager_id || ''}
            options={[
              { label: t(i18nKeys.company.site_manager_none), value: '' },
              ...siteManagers
                .filter(manager => manager.is_used)
                .map(manager => ({
                  label: manager.name || manager.email,
                  value: manager.id,
                })),
            ]}
            onChange={option => {
              const value = option.value as string | number;
              setFormData({ ...formData, site_manager_id: value ? Number(value) : null });
            }}
            isEdit={!loadingSiteManagers}
            width="100%"
            align="left"
            size="md"
          />
          {loadingSiteManagers && <LoadingText>{t(i18nKeys.company.loading)}</LoadingText>}
        </FormField>
      </FormStack>
    </ModalShell>
  );
};
